/**
 * SettingsModal - Modal for app settings
 */

import React from 'react';
import { motion } from 'framer-motion';
import { X, Settings, Sparkles, Eye, Layers, Zap } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';

const SettingsModal = () => {
  const { settings, updateSettings, closeSettings } = useMapStore();

  const settingsOptions = [
    {
      key: 'autoConnect',
      label: 'Auto-Connect Nodes',
      description: 'Automatically create semantic connections between related nodes using AI',
      icon: Zap,
      color: 'text-blue-400',
    },
    {
      key: 'devilsAdvocate',
      label: "Devil's Advocate",
      description: 'AI will challenge your ideas with counter-arguments to strengthen your thinking',
      icon: Sparkles,
      color: 'text-red-400',
    },
    {
      key: 'semanticZoom',
      label: 'Semantic Zoom',
      description: 'Show different detail levels based on zoom level',
      icon: Layers,
      color: 'text-purple-400',
    },
    {
      key: 'showGhostNodes',
      label: 'Show Ghost Nodes',
      description: 'Display AI-suggested nodes that might fill knowledge gaps',
      icon: Eye,
      color: 'text-green-400',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <Settings size={20} className="text-gray-400" />
            <h2 className="text-lg font-semibold text-white">Settings</h2>
          </div>
          <button
            onClick={closeSettings}
            className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Settings List */}
        <div className="p-4 space-y-3">
          {settingsOptions.map(({ key, label, description, icon: Icon, color }) => (
            <div
              key={key}
              className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
              onClick={() => updateSettings({ [key]: !settings[key] })}
            >
              <div className={`p-2 rounded-lg bg-gray-700/50 ${color}`}>
                <Icon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{label}</span>
                  <div
                    className={`w-10 h-5 rounded-full transition-colors ${
                      settings[key] ? 'bg-indigo-600' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`w-4 h-4 mt-0.5 rounded-full bg-white transition-transform ${
                        settings[key] ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-800 bg-gray-800/30">
          <p className="text-xs text-gray-500 text-center">
            Settings are saved automatically
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SettingsModal;
